import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import { utilities } from 'utils/utilities';

/**
 * Landing page for the Stripe Connect onboarding return URL.
 * Stripe redirects here after the artist completes (or exits) the hosted onboarding flow.
 * Calls the refresh endpoint to sync the latest account state, then navigates back to billing.
 */
const OnboardingReturnPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const artistId = utilities.getUserId();

    if (!artistId) {
      navigate('/login', { replace: true });
      return;
    }

    const refresh = async () => {
      try {
        await ApiService.refreshArtistOnboardingState(artistId);
      } catch {
        // Non-fatal — still navigate back so the artist can see the current state
        setError('Could not refresh your onboarding status. Your progress has been saved — check the Billing page for your current status.');
      } finally {
        // Short delay so any error message is readable before redirecting
        setTimeout(() => {
          navigate(`/artist/${artistId}/billing`, { replace: true });
        }, error ? 3000 : 0);
      }
    };

    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center space-y-4">
        {error ? (
          <>
            <div className="text-3xl">⚠️</div>
            <p className="text-sm text-gray-600">{error}</p>
            <p className="text-xs text-gray-400">Redirecting you back to billing…</p>
          </>
        ) : (
          <>
            <div className="text-3xl animate-pulse">🔄</div>
            <p className="text-base font-medium text-gray-800">Finishing up…</p>
            <p className="text-sm text-gray-500">
              Syncing your Stripe account status. You'll be redirected in a moment.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingReturnPage;
